import { UseFormReturn, useWatch } from 'react-hook-form';
import { IFieldElem } from '@models/IFormData';
import { FormulaManager, hasUnsupportedRemoteFormulaData } from '@utils/managers/FormulaManager';
import { handlerFieldReplaceWatch } from '@utils/fieldWatchUtils';
import { RefObject, useContext, useEffect, useMemo, useState } from 'react';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { EnsureExternalDataSourceLoaded, extractExternalDataSourceNames } from '@/utils/externalDataSourceUtils';
import type { IFormulaTraceTarget } from '@/context/DocumentFormulaTraceContext';
import { useFormulaTracePublisher } from './useFormulaTracePublisher';

export interface UseFormulaReplaceOptions {
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
    trace?: IFormulaTraceTarget;
}

export function useFormulaReplace(
    rules: string | undefined,
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<any>,
    isEdit: boolean,
    isNew: boolean,
    activated: RefObject<boolean>,
    options?: UseFormulaReplaceOptions,
) {
    const ensureExternalDataSourceLoaded = options?.ensureExternalDataSourceLoaded;
    const runtime = useContext(DocumentRuntimeContext);
    const remoteDataBlocked =
        runtime.mode === 'supportPreview' && hasUnsupportedRemoteFormulaData(rules, runtime.dataSource);
    const externalSourceNames = useMemo(() => (!isNew ? extractExternalDataSourceNames(rules) : []), [rules, isNew]);
    const [externalDataSourceReady, setExternalDataSourceReady] = useState(externalSourceNames.length === 0);

    const manager = useMemo(() => {
        if (!rules || remoteDataBlocked) return null;
        const m = new FormulaManager(rules, runtime.dataSource, runtime.requestCache);
        m.Init(fields, formMethods);
        return m;
    }, [rules, fields, formMethods, remoteDataBlocked, runtime.dataSource, runtime.requestCache]);
    const { publishTrace, removeTrace } = useFormulaTracePublisher(
        rules,
        fields,
        manager,
        externalSourceNames,
        options?.trace,
    );

    const [value, setValue] = useState<any>();

    useEffect(() => {
        if (externalSourceNames.length === 0 || !ensureExternalDataSourceLoaded) {
            setExternalDataSourceReady(true);
            return;
        }

        setExternalDataSourceReady(false);
    }, [externalSourceNames, ensureExternalDataSourceLoaded]);

    useEffect(() => {
        if (externalSourceNames.length === 0 || !ensureExternalDataSourceLoaded) {
            return;
        }

        let canceled = false;
        Promise.all(externalSourceNames.map((name) => ensureExternalDataSourceLoaded(name))).finally(() => {
            if (!canceled) {
                setExternalDataSourceReady(true);
            }
        });

        return () => {
            canceled = true;
        };
    }, [externalSourceNames, ensureExternalDataSourceLoaded]);

    useEffect(() => {
        if (!externalDataSourceReady) {
            return;
        }

        let canceled = false;
        if (manager && rules) {
            manager
                .ReplaceFormulaValues(true, true, undefined, false)
                .then((d) => {
                    if (canceled) return;
                    publishTrace(d, 'evaluated');
                    if (activated.current && d) setValue(d);
                })
                .catch((error: unknown) => {
                    if (canceled) return;
                    publishTrace(undefined, 'error', error instanceof Error ? error.message : String(error));
                    console.error(error);
                });
        } else if (rules && remoteDataBlocked) {
            publishTrace(undefined, 'fallback', 'Session-scoped источник для серверной функции формулы не настроен.');
        } else if (!rules) {
            removeTrace();
        }

        return () => {
            canceled = true;
        };
    }, [externalDataSourceReady, manager, rules, activated, publishTrace, remoteDataBlocked, removeTrace]);

    const watchFields = useMemo(() => manager?.GetWatchFields() ?? [], [manager]);
    const watch = useWatch({
        name: watchFields,
    });

    useEffect(() => {
        if (!externalDataSourceReady) {
            return;
        }

        if (rules && manager) {
            void handlerFieldReplaceWatch(watch, isEdit, isNew, manager, setValue, (result) => {
                publishTrace(result, 'evaluated', undefined, watch);
            }).catch((error: unknown) => {
                publishTrace(undefined, 'error', error instanceof Error ? error.message : String(error), watch);
                console.error(error);
            });
        }
    }, [watch, isEdit, isNew, rules, manager, externalDataSourceReady, publishTrace]);

    return value;
}
